import Axios from "axios";
import { StudioAttributes } from "@/interfaces/studioAttributes";
import { AuthorizationList, StudioState } from "@/interfaces/studioState";
import { ManualSignUp } from "@/interfaces/user";
import BackgroundSettings from "@/interfaces/studioBackground";

export class StudioService {
    public static async getStudio(
        studioId: string,
        token: string
    ): Promise<{ studio: StudioState; status: number }> {
        const uri: string = `/api/studio/by_id/${studioId}`;

        return await Axios.get(uri, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then(response => {
                return {
                    studio: response.data,
                    status: response.status,
                };
            })
            .catch(error => {
                return {
                    studio: error.response.data.detail,
                    status: error.response.status,
                };
            });
    }

    public static async updateAttributes(
        attributes: { studioId: string; attributes: Partial<StudioAttributes> },
        token: string
    ) {
        const uri = "/api/studio/attributes";
        const response = await Axios.put(uri, attributes, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.status === 204;
    }

    public static async quickConnect(
        id: string
    ): Promise<{ data: string; status: number }> {
        const uri: string = "/api/quick_connect_code/" + id;

        return await Axios.get(uri)
            .then(response => {
                return {
                    data: response.data.studioId,
                    status: response.status,
                };
            })
            .catch(error => {
                return {
                    data: error.response.data.detail,
                    status: error.response.status,
                };
            });
    }

    public static async createQuickConnectCode(
        studioId: string,
        validTo: string,
        token: string
    ) {
        const uri: string = "/api/quick_connect_code";
        return await Axios.post(
            uri,
            { studioId, validTo },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then(response => {
                return response;
            })
            .catch(error => {
                return error.response;
            });
    }

    public static async deleteQuickConnectCode(id: string, token: string) {
        const uri: string = "/api/quick_connect_code";
        const response = await Axios.delete(uri, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            data: {
                id,
            },
        });
        return response.data;
    }

    public static async addStudio(token: string, attributes: StudioAttributes) {
        const uri: string = "/api/studio/add";
        return await Axios.post(
            uri,
            { studioName: "NewStudio", attributes: attributes },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then(response => {
                return response;
            })
            .catch(error => {
                return error.response;
            });
    }
    public static async removeStudio(token: string, studioId: string) {
        const uri: string = "/api/studio/remove";
        return await Axios.delete(uri, {
            data: { studioId: studioId },

            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then(response => {
                return response;
            })
            .catch(error => {
                return error.response;
            });
    }

    public static async changeName(
        studioId: string,
        studioName: string,
        token: string
    ) {
        const uri: string = "/api/studio/name";
        return await Axios.put(
            uri,
            { studioId: studioId, studioName: studioName },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then(response => {
                return response;
            })
            .catch(error => {
                return error.response;
            });
    }
    public static async changeAuthorization(
        listType: string,
        studioId: string,
        token: string,
        file?: File
    ): Promise<{
        data: { detail?: string; emails?: string[] };
        status: number;
    }> {
        const uri = "/api/studio/authorization";

        const formdata = new FormData();
        if (file) formdata.append("file", file);
        const response = await Axios.put(uri, file ? formdata : null, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: { studioId: studioId, listType: listType },
        })
            .then(response => {
                return {
                    data: { emails: response.data },
                    status: response.status,
                };
            })
            .catch(error => {
                console.log(error);
                return error.response;
            });
        return response;
    }
    public static async changeAuthorizationActive(
        studioId: string,
        state: boolean,
        token: string
    ) {
        const uri: string = "/api/studio/activateAuthorization";
        return await Axios.put(
            uri,
            { studioId: studioId, authorizationActive: state },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then(response => {
                return response;
            })
            .catch(error => {
                return error.response;
            });
    }

    public static async authorizationLists(
        studioId: string,
        token: string
    ): Promise<AuthorizationList> {
        const uri: string = "/api/studio/authorizationLists";

        return await Axios.get(uri, {
            params: { studioId: studioId },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then(response => {
                return response.data;
            })
            .catch(error => {
                return error.response.data;
            });
    }
    public static async authorizationActive(
        studioId: string
    ): Promise<boolean> {
        const uri: string = "/api/studio/authorizationActive";

        return await Axios.get(uri, {
            params: { studioId: studioId },
        })
            .then(response => {
                return response.data;
            })
            .catch(error => {
                return error;
            });
    }
    public static async checkAuthorizationLists(
        studioId: string,
        email: string
    ): Promise<{ allowed: boolean; blocked: boolean }> {
        const uri: string = "/api/studio/checkAuthorizationLists";

        return await Axios.get(uri, {
            params: { studioId: studioId, email: email },
        })
            .then(response => {
                return response.data;
            })
            .catch(error => {
                return error;
            });
    }
    public static async signUpMeta(
        studioId: string,
        payload: ManualSignUp
    ): Promise<{ manualRegistrationComplete: boolean }> {
        const uri: string = "/api/studio/signUpMeta";
        return await Axios.put(
            uri,
            { payload },
            {
                params: { studioId: studioId },
            }
        )
            .then(response => {
                return response.data;
            })
            .catch(error => {
                return error.response;
            });
    }

    public static async changeBackgroundSettings(
        studioId: string,
        backgroundSettings: BackgroundSettings | null,
        token: string
    ): Promise<{ studio: StudioState; status: number }> {
        const uri: string = "/api/studio/backgroundSettings";
        return await Axios.put(
            uri,
            {
                studioId: studioId,
                backgroundSettings: backgroundSettings,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then(response => {
                return { studio: response.data, status: response.status };
            })
            .catch(error => {
                return error.response;
            });
    }
}
